// TelcoTrack — týmové poznámky u nedodělku (parita Zařízení / Úřady povinnosti)
// Popis při vytvoření = první položka historie (`note` je zrcadlo pro list).

export interface NedodelekNoteEntry {
  id: string;
  text: string;
  createdAt: string;
  authorEmail: string;
  authorName: string | null;
}

const MAX_NOTE_LENGTH = 4000;
export const NEDODELEK_NOTE_LEGACY_ID = 'legacy-note';

function randomNedodelekNoteId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function asTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function asIso(value: unknown, fallback: string): string {
  if (typeof value === 'string' && value.trim()) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }
  return fallback;
}

export function parseNedodelekNotesEntries(raw: unknown): NedodelekNoteEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: NedodelekNoteEntry[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const o = item as Record<string, unknown>;
    const text = asTrimmedString(o['text']);
    if (!text) continue;
    const id = asTrimmedString(o['id']) || randomNedodelekNoteId();
    out.push({
      id,
      text,
      createdAt: asIso(o['createdAt'] ?? o['created_at'], new Date(0).toISOString()),
      authorEmail: asTrimmedString(o['authorEmail'] ?? o['author_email']).toLowerCase(),
      authorName: asTrimmedString(o['authorName'] ?? o['author_name']) || null,
    });
  }
  return out;
}

/**
 * Historie pro UI: strukturované entries (nejnovější nahoře),
 * nebo legacy `note` jako jediná / první položka.
 */
export function coalesceNedodelekNotesForDisplay(
  entriesRaw: unknown,
  legacyNote?: string | null,
  fallbackCreatedAt?: string,
  authorEmail?: string | null,
): NedodelekNoteEntry[] {
  const entries = parseNedodelekNotesEntries(entriesRaw);
  if (entries.length > 0) {
    return [...entries].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }
  const legacy = (legacyNote ?? '').trim();
  if (!legacy) return [];
  return [
    {
      id: NEDODELEK_NOTE_LEGACY_ID,
      text: legacy,
      createdAt: asIso(fallbackCreatedAt, new Date(0).toISOString()),
      authorEmail: (authorEmail ?? '').trim().toLowerCase(),
      authorName: null,
    },
  ];
}

export function buildNedodelekNoteEntry(input: {
  text: string;
  authorEmail: string | null;
  authorName?: string | null;
  createdAt?: string;
  id?: string;
}): NedodelekNoteEntry {
  const text = input.text.trim().slice(0, MAX_NOTE_LENGTH);
  if (!text) {
    throw new Error('Poznámka nesmí být prázdná.');
  }
  return {
    id: input.id?.trim() || randomNedodelekNoteId(),
    text,
    createdAt: input.createdAt ?? new Date().toISOString(),
    authorEmail: (input.authorEmail ?? '').trim().toLowerCase(),
    authorName: (input.authorName ?? '').trim() || null,
  };
}

/** Při appendu zachovej legacy popis jako první chronologickou položku. */
export function appendNedodelekNoteEntry(
  entriesRaw: unknown,
  next: NedodelekNoteEntry,
  legacyNote?: string | null,
  fallbackCreatedAt?: string,
  authorEmail?: string | null,
): NedodelekNoteEntry[] {
  let entries = parseNedodelekNotesEntries(entriesRaw);
  if (entries.length === 0) {
    const legacy = (legacyNote ?? '').trim();
    if (legacy) {
      entries = [
        {
          id: randomNedodelekNoteId(),
          text: legacy,
          createdAt: asIso(fallbackCreatedAt, new Date(0).toISOString()),
          authorEmail: (authorEmail ?? '').trim().toLowerCase(),
          authorName: null,
        },
      ];
    }
  }
  return [...entries, next];
}

export function canModifyNedodelekNote(
  entry: NedodelekNoteEntry,
  caller: { email: string | null; isAdmin: boolean },
): boolean {
  if (caller.isAdmin) return true;
  const callerEmail = (caller.email ?? '').trim().toLowerCase();
  const authorEmail = (entry.authorEmail ?? '').trim().toLowerCase();
  if (callerEmail && authorEmail && callerEmail === authorEmail) return true;
  return false;
}

export function updateNedodelekNoteEntry(
  entriesRaw: unknown,
  noteId: string,
  nextText: string,
  legacyNote?: string | null,
  fallbackCreatedAt?: string,
  authorEmail?: string | null,
): NedodelekNoteEntry[] {
  const text = nextText.trim().slice(0, MAX_NOTE_LENGTH);
  if (!text) {
    throw new Error('Poznámka nesmí být prázdná.');
  }
  const entries = coalesceNedodelekNotesForDisplay(
    entriesRaw,
    legacyNote,
    fallbackCreatedAt,
    authorEmail,
  );
  const idx = entries.findIndex((n) => n.id === noteId.trim());
  if (idx < 0) {
    throw new Error('Poznámka nenalezena.');
  }
  const next = [...entries];
  next[idx] = {
    ...next[idx],
    id: next[idx].id === NEDODELEK_NOTE_LEGACY_ID ? randomNedodelekNoteId() : next[idx].id,
    text,
  };
  // Persist chronological (oldest first) order in storage.
  return [...next].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export function deleteNedodelekNoteEntry(
  entriesRaw: unknown,
  noteId: string,
  legacyNote?: string | null,
  fallbackCreatedAt?: string,
  authorEmail?: string | null,
): NedodelekNoteEntry[] {
  const entries = coalesceNedodelekNotesForDisplay(
    entriesRaw,
    legacyNote,
    fallbackCreatedAt,
    authorEmail,
  );
  const id = noteId.trim();
  if (!entries.some((n) => n.id === id)) {
    throw new Error('Poznámka nenalezena.');
  }
  return entries
    .filter((n) => n.id !== id)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** List/detail mirror: chronologically first note text. */
export function primaryNedodelekNoteText(entries: NedodelekNoteEntry[]): string {
  if (!entries.length) return '';
  const chronological = [...entries].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  return chronological[0]?.text?.trim() || '';
}

export const NEDODELEK_NOTE_MAX_LENGTH = MAX_NOTE_LENGTH;

/** Body for `POST /objects/nedodelky/:id/notes`. */
export function buildAddNedodelekNotePayload(text: string): { text: string } {
  const trimmed = text.trim().slice(0, MAX_NOTE_LENGTH);
  if (!trimmed) {
    throw new Error('Poznámka nesmí být prázdná.');
  }
  return { text: trimmed };
}
